/**
 * Serves as the base validator for all other validators.
 *
 */
import Backbone from 'backbone';

var Events = {
    ValidationFailure: 'VALIDATION_FAILURE_EVENT',
    ValidationSuccess: 'VALIDATION_SUCCESS_EVENT'
};

var ValidationResults = {
    Pending: 'VALIDATION_PENDING' // Result used to indicate that validation is asynchronous and pending.
};

var Validator = Backbone.Model.extend({
    initialize: function(args) {
        this.initializeValidator(args);
    },

    /**
     * Parent class specific initialization function used to get around limiations of inheritance
     * in javascript.
     * @param args
     */
    initializeValidator: function(args) {
        if (!args.elementId && !args.element) {
            throw 'Validator missing element';
        }

        if (args.element) {
            // prefer element over id
            this.$inputField = jQuery(args.element);
        } else {
            this.$inputField = jQuery('#' + args.elementId); // Cached jquery object
        }

        this.parser = args.parser; // Parser used to parse the input
        this.formatter = args.formatter; // The formatter object used to format the input
        this.reporter = args.reporter; // The ErrorReporterObject used to display the error.

        var that = this;
        this.$inputField.bind('change.validator', function(evt) {
            that.onValueChanged(that.getTargetValue());
        });

        this.$inputField.bind('keydown.validator', function(evt) {
            if (evt.keyCode == 9) {
                // Tab key
                that.onValueChanged(that.getTargetValue());
            } else {
                that.onInputChanged(that.getTargetValue());
            }
        });
    },

    /**
     * Forces validation on the input field now.  Useful for validating a field when the user hasn't
     * modified it.
     *
     * returns undefined if value passes validation. Return the validation error otherwise.
     */
    validateInputNow: function() {
        return this.onValueChanged(this.getTargetValue());
    },

    /**
     * Handler called anytime the content of the field as changed.
     * @param value the current value in the field.
     */
    onInputChanged: function(value) {
        this.clearReport();
    },

    /**
     * Handler called anytime the input field has changed. Only gets fired when tabbing out or lose focus.
     * @param value the current value in the field.
     */
    onValueChanged: function(value) {
        // Validate will return an error string if something failed. Otherwise returns undefined.
        var result = this.validateInput(value);
        if (result != ValidationResults.Pending) {
            if (result) {
                this.triggerFailureEvent(value, result);
                this.report(value, result);
            } else {
                this.triggerSuccessEvent(value);
                this.clearReport();
            }

            return result;
        } else {
            // Validation is asynchronous. The child Validator will report success or failure. Do nothing.
        }

        // Don't return anything for success
    },

    /**
     * Triggers a validatoin success event.
     * @param value the value that was validated.
     */
    triggerSuccessEvent: function(value) {
        this.trigger(Events.ValidationSuccess, this.makeValidationResult(value));
    },

    /**
     * Triggers an error event.
     * @param value the value that failed validation.
     * @param result the error encountered.
     */
    triggerFailureEvent: function(value, result) {
        this.trigger(Events.ValidationFailure, this.makeValidationResult(value, result));
    },

    /**
     * Packages the results of the validation as an object.
     * @param value the value that was validated
     * @param result the error that was reported, or undefined if validation is successful.
     */
    makeValidationResult: function(value, result) {
        return {
            validator: this,
            error: result,
            enteredValue: value,
            parsedValue: this.parse(value),
            formattedValue: this.format(value)
        };
    },

    /**
     * Parses the value using the Validators designated parser. This may also include converting
     * the value to storage format
     *
     * @param value the value to parse.
     * @returns the parsed value or null if error.
     */
    parse: function(value) {
        if (this.parser) {
            try {
                return this.parser.parse(value);
            } catch (err) {
                return null;
            }
        } else {
            return value;
        }
    },

    /**
     * Reformats the value using the Validator's designated formatter. Used for reformatting
     * values entered by the user that pass validation but need to be displayed differently, such as
     * URLs which tack on the http://
     *
     * @param value the value to format.
     * @returns the formatted value
     */
    format: function(value) {
        if (this.formatter) {
            return this.formatter.format(value);
        } else {
            return value;
        }
    },

    /**
     * Reports the error, usually using some kind of visual interface.
     * @param value the value that was validated.
     * @param result the error key for the validation error.
     */
    report: function(value, result) {
        if (this.reporter) {
            this.reporter.report(value, result);
        }
    },

    clearReport: function() {
        if (this.reporter) {
            this.reporter.remove();
        }
    },

    /**
     * This is the default validate method, which does no validation
     * Typically, you would override this in the derived class
     */
    validateInput: function(value) {
        return '';
    },

    /**
     * Cleanup.
     */
    destroy: function() {
        if (this.reporter) {
            this.reporter.remove();
        }

        if (this.$inputField) {
            this.$inputField.unbind('change.validator');
            this.$inputField.unbind('keydown.validator');
        }
    },

    getTargetValue: function() {
        return this.$inputField.val() || this.$inputField.text();
    }
});

Validator.Events = Events;
Validator.ValidationResults = ValidationResults;

export default Validator;
